import { useContext, useEffect, useState } from "react";
import { Button, Dropdown } from "antd";
import { useNavigate } from "react-router-dom";
import { exportPatients, getPatients } from "../../apis/patient";
import SearchBar from "../../components/SearchBar";
import Modal from "../../components/Modal";
import AddAppointment from "../visits/AddAppointment";
import PaymentModal, { PaymentData } from "../../components/PaymentModal";
import { savePatientTransaction } from "../../apis/patientTransaction";
import { useNotification } from "../../NotificationContext";
import { LanguageContext } from "../../providers/LanguageProvider";
import CommonPagination from "../../components/CommanPagination";
import { format } from "date-fns";
import CreateEpisodeModal from "../episodes/CreateEpisodeModal";
import ConsentGeneratorDrawer from "../consent/ConsentGeneratorDrawer";

const buildPatientPrefill = (patient: any): Record<string, string> => {
  const prefill: Record<string, string> = {};
  const fullName = [patient.firstName, patient.middleName, patient.lastName].filter(Boolean).join(" ").trim();
  for (const k of ["patient_name", "name", "full_name", "patient"]) prefill[k] = fullName;
  if (patient.age)          for (const k of ["age", "patient_age", "age_years"]) prefill[k] = patient.age;
  if (patient.address)      for (const k of ["address", "patient_address", "residence"]) prefill[k] = patient.address;
  if (patient.phone)        for (const k of ["phone", "mobile", "contact", "phone_number", "mobile_number"]) prefill[k] = patient.phone;
  if (patient.gender)       for (const k of ["gender", "sex"]) prefill[k] = patient.gender;
  if (patient.aadharNumber) for (const k of ["aadhar_number", "aadhar", "aadhaar"]) prefill[k] = patient.aadharNumber;
  return prefill;
};

const PatientList = () => {
  const [userId, setUserId] = useState<number>();
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(
    null
  );
  const [meta, setMeta] = useState<any>();
  const [patients, setPatients] = useState<Array<any>>();
  const navigate = useNavigate();
  const [isAppointmentModalOpen, setAddAppointmentModalVisibility] =
    useState<boolean>(false);
  const [isPaymentModalOpen, setPaymentModalVisibility] =
    useState<boolean>(false);
  const [selectedPatientIdForPayment, setSelectedPatientIdForPayment] =
    useState<number | null>(null);
  const [selectedPatientName, setSelectedPatientName] =
    useState<string>("");
  const [paymentTotal, setPaymentTotal] = useState<number>(0);
  const [isEpisodeModalOpen, setEpisodeModalOpen] = useState<boolean>(false);
  const [selectedPatientIdForEpisode, setSelectedPatientIdForEpisode] =
    useState<number | null>(null);
  const [selectedPatientNameForEpisode, setSelectedPatientNameForEpisode] =
    useState<string>("");
  const [selectedPatientGenderForEpisode, setSelectedPatientGenderForEpisode] =
    useState<string>("");
  const [consentPatient, setConsentPatient] = useState<any | null>(null);
  const { translate: t } = useContext(LanguageContext);
  const { showNotification } = useNotification();

  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});

  useEffect(() => {
    fetchPatients({ per_page: 10 });
  }, []);

  const handleSearch = (value: string) => {
    const updated: Record<string, any> = { ...activeFilters, page: 1 };
    if (value) updated.search = value; else delete updated.search;
    setActiveFilters(updated);
    fetchPatients({ per_page: 10, ...updated });
  };

  const fetchPatients = (params = {}) => {
    getPatients(params)
      .then((res: any) => {
        setPatients(res.data.data);
        setMeta(res.data.meta);
      })
      .catch((err: any) => {
        console.log("err", err);
      });
  };

  const handleScheduleAppointment = (id: number) => {
    setSelectedPatientId(id);
    const user = window.localStorage.getItem("user");
    if (user) {
      const loggedInUser = JSON.parse(user);
      setUserId(loggedInUser.id);
    }
    setAddAppointmentModalVisibility(true);
  };

  const handleCloseModal = () => {
    setAddAppointmentModalVisibility(false);
    setSelectedPatientId(null);
  };

  const handleShowTransactions = (patientId: number) => {
    navigate(`/patients/${patientId}/transactions`);
  };

  const handleAddPayment = (patientId: number) => {
    const patient = patients?.find((p) => p.id === patientId);
    const patientName = patient
      ? `${patient.firstName || ""} ${patient.lastName || ""}`.trim()
      : "";
    setSelectedPatientIdForPayment(patientId);
    setSelectedPatientName(patientName);
    setPaymentTotal(0);
    setPaymentModalVisibility(true);
  };

  const handleCreateEpisode = (patientId: number) => {
    const patient = patients?.find((p) => p.id === patientId);
    const patientName = patient
      ? `${patient.firstName || ""} ${patient.lastName || ""}`.trim()
      : "";
    setSelectedPatientIdForEpisode(patientId);
    setSelectedPatientNameForEpisode(patientName);
    setSelectedPatientGenderForEpisode(patient?.gender || "");
    setEpisodeModalOpen(true);
  };

  const handleClosePaymentModal = () => {
    setPaymentModalVisibility(false);
    setSelectedPatientIdForPayment(null);
    setSelectedPatientName("");
    setPaymentTotal(0);
  };

  const handleCloseEpisodeModal = () => {
    setEpisodeModalOpen(false);
    setSelectedPatientIdForEpisode(null);
    setSelectedPatientNameForEpisode("");
    setSelectedPatientGenderForEpisode("");
  };

  const handlePaymentSave = async (paymentData: PaymentData) => {
    if (!selectedPatientIdForPayment) return;

    try {
      const paymentModes = [
        { mode: "cash", amount: paymentData.cash },
        { mode: "upi", amount: paymentData.upi },
        { mode: "card", amount: paymentData.card },
        { mode: "cheque", amount: paymentData.cheque },
        { mode: "online", amount: paymentData.online },
      ];

      // Create a transaction for each non-zero payment mode
      const savePromises = paymentModes
        .filter((p) => p.amount > 0)
        .map((payment) =>
          savePatientTransaction({
            patientId: selectedPatientIdForPayment,
            amount: payment.amount,
            paymentMode: payment.mode,
            transactionId: payment.mode !== "cash" ? paymentData.transactionRef || undefined : undefined,
            note: paymentData.paymentNote || "",
            date: format(new Date(), "yyyy-MM-dd HH:mm").toString(),
          })
        );

      await Promise.all(savePromises);

      showNotification(
        "success",
        "Payment saved successfully",
        "",
        "topRight"
      );
      handleClosePaymentModal();
    } catch (err: any) {
      showNotification(
        "error",
        "Failed to save payment",
        err?.message || "",
        "topRight"
      );
    }
  };

  const onPaginationChange = (page: number, pageSize?: number) => {
    const updated = { ...activeFilters, page, per_page: pageSize ?? 10 };
    setActiveFilters(updated);
    fetchPatients(updated);
  };

  const downloadPatientList = () => {
    exportPatientsList({ ...activeFilters });
  };

  const exportPatientsList = (params: any) => {
    exportPatients(params).then((res: any) => {
      downloadFile(res.data);
    });
  };

  const downloadFile = (data: any) => {
    const blob = new Blob([data], { type: "application/vnd.ms-excel" });
    // const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = window.URL.createObjectURL(blob);
    const downloadLink = document.createElement("a");
    downloadLink.href = url;
    const today = format(new Date(), "dd_MM_yyyy_hh_mm");
    downloadLink.download = "patients-" + today + ".xlsx";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  return (
    <div className="px-2 lg:px-8">
      {/* header for mobile screen */}
      <div className="flex lg:hidden">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {t("patient")}
          </h1>
        </div>
        <div className="flex flex-row-reverse w-full">
          <div>
            <button
              type="button"
              onClick={() => navigate("/patients/new")}
              className="block px-3 py-2 text-sm font-semibold text-center text-white rounded-md shadow-sm bg-primary hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hover"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4.5v15m7.5-7.5h-15"
                />
              </svg>
            </button>
          </div>
          <div>
            <button
              type="button"
              onClick={() => downloadPatientList()}
              className="block px-3 py-2 mx-2 text-sm font-semibold text-center text-white capitalize rounded-md shadow-sm bg-primary hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hover"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 8.25H7.5a2.25 2.25 0 0 0-2.25 2.25v9a2.25 2.25 0 0 0 2.25 2.25h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25H15M9 12l3 3m0 0 3-3m-3 3V2.25"
                />
              </svg>
            </button>
          </div>
          <SearchBar placeholder="Search patient name..." onSearch={handleSearch} />
        </div>
      </div>
      <div className="hidden sm:items-center lg:flex">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {t("patient")}
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {t("patient_description")}
          </p>
        </div>
        <div className="mx-2 mt-4 sm:mt-0 sm:ml-16 sm:flex gap-2">
          <SearchBar placeholder="Search patient name..." onSearch={handleSearch} />
          <button
            type="button"
            onClick={() => navigate("/patients/new")}
            className="block px-3 py-2 text-sm font-semibold text-center text-white rounded-md shadow-sm bg-primary hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hover"
          >
            {t("add_patient")}
          </button>
          <button
            type="button"
            onClick={() => downloadPatientList()}
            className="block px-3 py-2 ml-2 text-sm font-semibold text-center text-white capitalize rounded-md shadow-sm bg-primary hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hover"
          >
            {t("export")}
          </button>
        </div>
      </div>
      <div className="flow-root mt-8">
        <div className="overflow-x-auto -mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block py-2 min-w-full align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden ring-1 ring-black ring-opacity-5 shadow sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      {t("name")}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t("email")}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t("phone")}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      MRN
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Age / Sex
                    </th>

                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 capitalize"
                    >
                      {t("actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {patients && patients.length > 0 ? (
                    patients.map((patient) => (
                      <tr key={patient.id}>
                        <td className="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 capitalize whitespace-nowrap sm:pl-6">
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-full overflow-hidden bg-gray-100 border border-gray-200 flex-shrink-0 flex items-center justify-center">
                              {patient.photoUrl ? (
                                <img
                                  src={patient.photoUrl}
                                  alt=""
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <span className="text-[10px] text-gray-400 font-semibold uppercase">
                                  {(patient.firstName?.[0] || "") +
                                    (patient.lastName?.[0] || "")}
                                </span>
                              )}
                            </div>
                            <span>
                              {patient.firstName + " " + patient.lastName}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {patient.email}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {patient.phone}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500 whitespace-nowrap font-mono">
                          {patient.clinicNumber || patient.registrationNumber || "—"}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {[patient.age ? `${patient.age}y` : null, patient.gender ? patient.gender.charAt(0).toUpperCase() : null].filter(Boolean).join(" / ") || "—"}
                        </td>

                        <td className="relative py-4 pr-4 pl-3 text-sm font-medium text-left whitespace-nowrap sm:pr-6">
                          <Dropdown
                            menu={{
                              items: [
                                {
                                  key: "Edit",
                                  label: (
                                    <a
                                      href={`/patients/${patient.id}`}
                                      rel="noopener noreferrer"
                                    >
                                      {t("edit")}
                                    </a>
                                  ),
                                },
                                {
                                  key: "Create Visit",
                                  label: (
                                    <a
                                      rel="noopener noreferrer"
                                      href={`/patients/${patient.id}/visits/types`}
                                    >
                                      {t("create_new_visit")}
                                    </a>
                                  ),
                                },
                                {
                                  key: "Schedule Appointment",
                                  label: (
                                    <span
                                      onClick={() =>
                                        handleScheduleAppointment(patient.id)
                                      }
                                    >
                                      {t("schedule_an_appointment")}
                                    </span>
                                  ),
                                },
                                {
                                  key: "Immunizations",
                                  label: (
                                    <a
                                      href={`/patients/${patient.id}/immunizations`}
                                      rel="noopener noreferrer"
                                    >
                                      Immunization Record
                                    </a>
                                  ),
                                },
                                {
                                  key: "Show Transactions",
                                  label: (
                                    <span
                                      onClick={() =>
                                        handleShowTransactions(patient.id)
                                      }
                                    >
                                      Show Transactions
                                    </span>
                                  ),
                                },
                                {
                                  key: "Add Payment",
                                  label: (
                                    <span
                                      onClick={() =>
                                        handleAddPayment(patient.id)
                                      }
                                    >
                                      Add Payment
                                    </span>
                                  ),
                                },
                                {
                                  key: "Create Episode",
                                  label: (
                                    <span
                                      onClick={() =>
                                        handleCreateEpisode(patient.id)
                                      }
                                    >
                                      Create Episode
                                    </span>
                                  ),
                                },
                                {
                                  key: "View Episodes",
                                  label: (
                                    <span
                                      onClick={() =>
                                        navigate(`/episodes?patient_id=${patient.id}`)
                                      }
                                    >
                                      View Episodes
                                    </span>
                                  ),
                                },
                                {
                                  key: "Generate Consent",
                                  label: (
                                    <span onClick={() => setConsentPatient(patient)}>
                                      Generate Consent
                                    </span>
                                  ),
                                },
                              ],
                            }}
                            placement="bottomLeft"
                          >
                            <Button>{t("actions")}</Button>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        className="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 sm:pl-6"
                      >
                        {t("Record Not Found")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <CommonPagination meta={meta} onChange={onPaginationChange} />
          </div>
        </div>
      </div>
      {isAppointmentModalOpen && selectedPatientId && (
        <Modal
          title="Appointment"
          onClose={() => handleCloseModal()}
          modalContent={
            <AddAppointment
              patientId={selectedPatientId}
              userId={userId}
              closeModal={() => handleCloseModal()}
            />
          }
        />
      )}
      {isPaymentModalOpen && selectedPatientIdForPayment && (
        <PaymentModal
          isOpen={isPaymentModalOpen}
          onClose={handleClosePaymentModal}
          total={paymentTotal}
          discount={0}
          patientId={selectedPatientIdForPayment}
          patientName={selectedPatientName}
          sourceType="pathology"
          onSave={handlePaymentSave}
          onPaymentSuccess={handleClosePaymentModal}
        />
      )}
      {isEpisodeModalOpen && selectedPatientIdForEpisode && (
        <Modal
          title="Create Episode"
          width="sm:max-w-3xl"
          onClose={handleCloseEpisodeModal}
          modalContent={
            <CreateEpisodeModal
              patientId={selectedPatientIdForEpisode}
              patientName={selectedPatientNameForEpisode}
              patientGender={selectedPatientGenderForEpisode}
              onClose={handleCloseEpisodeModal}
              onCreated={handleCloseEpisodeModal}
            />
          }
        />
      )}
      {consentPatient && (
        <ConsentGeneratorDrawer
          open={!!consentPatient}
          onClose={() => setConsentPatient(null)}
          patientId={consentPatient.id}
          prefill={buildPatientPrefill(consentPatient)}
        />
      )}
    </div>
  );
};

export default PatientList;
